import React, { useCallback, useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';

function formatarData(valor) {
  if (!valor) return '—';

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(valor));
  } catch {
    return '—';
  }
}

function labelPerfil(perfil) {
  const labels = {
    participante: 'Participante',
    coach: 'Coach / Mentor / Psicólogo',
    admin: 'Administrador'
  };

  return labels[perfil] || perfil || '—';
}

function labelStatus(status) {
  const labels = {
    valido: 'Válido',
    utilizado: 'Utilizado',
    expirado: 'Expirado',
    revogado: 'Revogado'
  };

  return labels[status] || status || '—';
}

function statusStyle(status) {
  const styles = {
    valido: {
      background: '#E8F6ED',
      color: '#246B3C'
    },
    utilizado: {
      background: '#EDF1F5',
      color: '#46505C'
    },
    expirado: {
      background: '#FFF4DF',
      color: '#8A5A00'
    },
    revogado: {
      background: '#FFF1F0',
      color: '#9F1C16'
    }
  };

  return styles[status] || styles.utilizado;
}

export default function AdminConvites() {
  const [convites, setConvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gerandoPerfil, setGerandoPerfil] = useState('');
  const [erro, setErro] = useState('');
  const [novoConvite, setNovoConvite] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const carregarConvites = useCallback(async () => {
    try {
      setErro('');

      const response = await fetch('/api/admin/convites', {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível carregar os convites.'
        );
      }

      setConvites(data.convites || []);
    } catch (e) {
      setErro(
        e.message || 'Não foi possível carregar os convites.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarConvites();
  }, [carregarConvites]);

  async function gerarConvite(perfil) {
    if (gerandoPerfil) return;

    setErro('');
    setCopiado(false);
    setNovoConvite(null);
    setGerandoPerfil(perfil);

    try {
      const response = await fetch(
        `/api/admin/convites/${perfil}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error || 'Não foi possível gerar o convite.'
        );
      }

      setNovoConvite(data.convite);
      await carregarConvites();
    } catch (e) {
      setErro(
        e.message || 'Não foi possível gerar o convite.'
      );
    } finally {
      setGerandoPerfil('');
    }
  }

  async function copiarLink() {
    if (!novoConvite?.url) return;

    try {
      await navigator.clipboard.writeText(novoConvite.url);
      setCopiado(true);

      window.setTimeout(() => {
        setCopiado(false);
      }, 2500);
    } catch {
      setErro(
        'Não foi possível copiar automaticamente. Selecione o link e copie manualmente.'
      );
    }
  }

  const mainStyle = {
    marginLeft: '220px',
    padding: '2rem',
    minHeight: '100vh',
    background: '#f4f4f4'
  };

  return (
    <div>
      <AdminSidebar />

      <main style={mainStyle}>
        <h1
          style={{
            margin: 0,
            color: '#242F3C',
            fontSize: '2rem',
            fontWeight: 700
          }}
        >
          Convites
        </h1>

        <p
          style={{
            marginTop: 8,
            marginBottom: 28,
            color: '#697382'
          }}
        >
          Gere links individuais para criação de contas conforme o perfil de acesso.
        </p>

        <section
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 18,
            marginBottom: 28
          }}
        >
          <div style={cardStyle}>
            <div>
              <h2 style={tituloCardStyle}>
                Participante
              </h2>

              <p style={descricaoCardStyle}>
                Cria uma conta de Participante e, após a ativação, inicia o Inventário de Competências para Vendas.
              </p>
            </div>

            <button
              type="button"
              onClick={() => gerarConvite('participante')}
              disabled={Boolean(gerandoPerfil)}
              style={{
                ...botaoPrincipalStyle,
                background:
                  gerandoPerfil === 'participante'
                    ? '#999'
                    : '#F36611',
                cursor: gerandoPerfil ? 'default' : 'pointer'
              }}
            >
              {gerandoPerfil === 'participante'
                ? 'Gerando...'
                : 'Gerar convite de Participante'}
            </button>
          </div>

          <div style={cardStyle}>
            <div>
              <h2 style={tituloCardStyle}>
                Coach / Mentor / Psicólogo
              </h2>

              <p style={descricaoCardStyle}>
                Cria uma conta profissional com acesso à área de acompanhamento e desenvolvimento de Participantes.
              </p>
            </div>

            <button
              type="button"
              onClick={() => gerarConvite('coach')}
              disabled={Boolean(gerandoPerfil)}
              style={{
                ...botaoPrincipalStyle,
                background:
                  gerandoPerfil === 'coach'
                    ? '#999'
                    : '#242F3C',
                cursor: gerandoPerfil ? 'default' : 'pointer'
              }}
            >
              {gerandoPerfil === 'coach'
                ? 'Gerando...'
                : 'Gerar convite de Coach'}
            </button>
          </div>
        </section>

        {novoConvite && (
          <section
            style={{
              background: '#F5FAFF',
              borderRadius: 12,
              padding: '1.5rem',
              marginBottom: 28,
              border: '1px solid #D9E8F5',
              boxShadow: '0 2px 10px rgba(36,47,60,0.04)'
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: '#242F3C',
                marginBottom: 6,
                fontSize: '1.05rem'
              }}
            >
              Convite gerado — {labelPerfil(novoConvite.perfil)}
            </div>

            <div
              style={{
                fontSize: '0.92rem',
                color: '#697382',
                marginBottom: 14
              }}
            >
              Copie este link agora. Por segurança, o token não poderá ser recuperado posteriormente.
            </div>

            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'stretch',
                flexWrap: 'wrap'
              }}
            >
              <input
                type="text"
                readOnly
                value={novoConvite.url || ''}
                onFocus={e => e.target.select()}
                style={{
                  flex: '1 1 420px',
                  minWidth: 0,
                  padding: '11px 12px',
                  borderRadius: 7,
                  border: '1px solid #C8D3DE',
                  background: '#fff',
                  fontSize: '0.95rem'
                }}
              />

              <button
                type="button"
                onClick={copiarLink}
                style={{
                  background: '#242F3C',
                  color: '#fff',
                  border: 'none',
                  padding: '11px 18px',
                  borderRadius: 7,
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {copiado ? 'Copiado!' : 'Copiar link'}
              </button>
            </div>

            <div
              style={{
                marginTop: 12,
                fontSize: '0.9rem',
                color: '#697382'
              }}
            >
              Validade até: {formatarData(novoConvite.expires_at)}
            </div>
          </section>
        )}

        {erro && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: '12px 14px',
              borderRadius: 8,
              background: '#FFF3F2',
              color: '#B42318',
              border: '1px solid #FECDCA',
              fontWeight: 600
            }}
          >
            {erro}
          </div>
        )}

        <section>
          <h2
            style={{
              color: '#242F3C',
              fontSize: '1.25rem',
              marginBottom: 14
            }}
          >
            Histórico de convites
          </h2>

          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              overflowX: 'auto',
              boxShadow: '0 2px 10px rgba(36,47,60,0.06)'
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: 820
              }}
            >
              <thead style={{ background: '#f7f7f7' }}>
                <tr>
                  <th style={th}>Perfil</th>
                  <th style={th}>Status</th>
                  <th style={th}>Gerado em</th>
                  <th style={th}>Válido até</th>
                  <th style={th}>Utilizado em</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#697382'
                      }}
                    >
                      Carregando convites...
                    </td>
                  </tr>
                ) : (
                  convites.map(convite => (
                    <tr key={convite.id}>
                      <td style={td}>
                        {labelPerfil(convite.perfil)}
                      </td>

                      <td style={td}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '6px 10px',
                            borderRadius: 999,
                            fontWeight: 700,
                            fontSize: '0.86rem',
                            ...statusStyle(convite.status)
                          }}
                        >
                          {labelStatus(convite.status)}
                        </span>
                      </td>

                      <td style={td}>
                        {formatarData(convite.created_at)}
                      </td>

                      <td style={td}>
                        {formatarData(convite.expires_at)}
                      </td>

                      <td style={td}>
                        {formatarData(convite.used_at)}
                      </td>
                    </tr>
                  ))
                )}

                {!loading && convites.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#697382'
                      }}
                    >
                      Nenhum convite gerado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

const cardStyle = {
  background: '#fff',
  borderRadius: 12,
  padding: '1.5rem',
  boxShadow: '0 2px 10px rgba(36,47,60,0.06)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 20
};

const tituloCardStyle = {
  margin: '0 0 8px 0',
  color: '#242F3C',
  fontSize: '1.25rem'
};

const descricaoCardStyle = {
  margin: 0,
  color: '#697382',
  lineHeight: 1.5
};

const botaoPrincipalStyle = {
  color: '#fff',
  border: 'none',
  padding: '12px 20px',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: '1rem',
  alignSelf: 'flex-start'
};

const th = {
  padding: 14,
  borderBottom: '2px solid #e6e6e6',
  textAlign: 'left',
  color: '#242F3C',
  whiteSpace: 'nowrap'
};

const td = {
  padding: 14,
  borderBottom: '1px solid #eef0f2',
  verticalAlign: 'middle'
};

